import { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack,
    Avatar, Input, Select, Option, Modal, ModalDialog, ModalClose,
    Textarea, IconButton, Alert, Chip, CircularProgress,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon     from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon           from '@mui/icons-material/AddRounded';
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded';
import EditRoundedIcon          from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon        from '@mui/icons-material/DeleteRounded';
import LockResetRoundedIcon     from '@mui/icons-material/LockResetRounded';
import PersonRoundedIcon        from '@mui/icons-material/PersonRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import BlockRoundedIcon         from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon   from '@mui/icons-material/CheckCircleRounded';
import EmailRoundedIcon         from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon         from '@mui/icons-material/PhoneRounded';
import VisibilityRoundedIcon    from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import SecurityRoundedIcon      from '@mui/icons-material/SecurityRounded';
import LogoutRoundedIcon        from '@mui/icons-material/LogoutRounded';
import RestoreRoundedIcon       from '@mui/icons-material/RestoreRounded';
import CameraAltRoundedIcon     from '@mui/icons-material/CameraAltRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PaidRoundedIcon          from '@mui/icons-material/PaidRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetEmployees,
    APIGetRoles,
    APIGetRoleDetail,
    APICreateEmployee,
    APIUpdateEmployee,
    APITrashEmployee,
    APIRestoreFromTrash,
    APIGetTrashedEmployees,
    APIDeleteEmployeePermanent,
    APIResetEmployeePassword,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import FireEmployeeModal from './FireEmployeeModal';
import { APIUploadAvatar, APIDeleteAvatar, APISetAdminSalary } from '../../api';


// ─── Утилиты ──────────────────────────────────────────────────────────────────
const getInitials = (acc) => {
    if (!acc) return '?';
    return ((acc.first_name?.[0] || '') + (acc.last_name?.[0] || '')).toUpperCase() || acc.email?.[0]?.toUpperCase() || '?';
};

const selectListboxSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    '& .MuiOption-root': {
        color: 'var(--joy-palette-text-primary)',
        fontSize: 13,
        '&:hover': { bgcolor: 'rgba(162,38,226,0.12)' },
        '&[aria-selected="true"]': { bgcolor: 'rgba(162,38,226,0.2)', color: 'var(--joy-palette-primary-300)', fontWeight: 600 },
    },
};

const ROLE_CONFIG = {
    photographer:       { label: 'Фотограф',          color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.1)',  icon: <PersonRoundedIcon sx={{ fontSize: 11 }} /> },
    manager:            { label: 'Руководитель',       color: '#f59e0b',                        bg: 'rgba(245,158,11,0.1)', icon: <ManageAccountsRoundedIcon sx={{ fontSize: 11 }} /> },
    admin:              { label: 'Админ точки',        color: '#3b82f6',                        bg: 'rgba(59,130,246,0.1)', icon: <AdminPanelSettingsRoundedIcon sx={{ fontSize: 11 }} /> },
    admin_photographer: { label: 'Админ + Фотограф',  color: '#f59e0b',                        bg: 'rgba(245,158,11,0.1)',  icon: <AdminPanelSettingsRoundedIcon sx={{ fontSize: 11 }} /> },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.055 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

function AvatarUploader({ employee, onUpdated, handleDelete }) {
    const { token, showToast } = useContext(AppContext);
    const [uploading, setUploading] = useState(false);
    const [localUrl, setLocalUrl] = useState(employee.avatar_url || null);
    const fileRef = useRef(null);

    const handlePick = () => fileRef.current?.click();

    const handleChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            showToast('Файл больше 5 МБ', 'danger');
            return;
        }
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            showToast('Допустимы только JPG / PNG / WEBP', 'danger');
            return;
        }

        setUploading(true);
        const res = await APIUploadAvatar(token, employee.id, file);
        setUploading(false);

        if (res?.error) {
            const msg = typeof res.error === 'string' ? res.error : (res.error.error || 'Ошибка загрузки');
            showToast(msg, 'danger');
            return;
        }

        setLocalUrl(res.avatar_url ? `${res.avatar_url}?t=${Date.now()}` : null);
        showToast('Аватарка обновлена ✓');
        if (onUpdated) onUpdated(res);

        // сбрасываем input, чтобы можно было выбрать тот же файл снова
        e.target.value = '';
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ position: 'relative' }}>
                <Avatar
                    sx={{ width: 64, height: 64, fontSize: 20, fontWeight: 700 }}
                    src={localUrl || undefined}
                >
                    {(employee.first_name?.[0] || '?').toUpperCase()}
                </Avatar>
                {uploading && (
                    <Box sx={{
                        position: 'absolute', inset: 0, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.5)',
                    }}>
                        <CircularProgress size="sm" />
                    </Box>
                )}
            </Box>

            <Stack spacing={0.6}>
                <Button
                    size="sm" variant="outlined" color="neutral"
                    onClick={handlePick} disabled={uploading}
                    startDecorator={<CameraAltRoundedIcon sx={{ fontSize: 14 }} />}
                    sx={{ "&:hover .MuiButton-startDecorator": { color: 'black !important' } }}
                >
                    {localUrl ? 'Сменить' : 'Загрузить'}
                </Button>
                {localUrl && (
                    <Button
                        size="sm" variant="plain" color="danger"
                        onClick={handleDelete} disabled={uploading}
                        startDecorator={<DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />}
                        sx={{ bgcolor: 'red' }}
                    >
                        Удалить фото
                    </Button>
                )}
            </Stack>

            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleChange}
                style={{ display: 'none' }}
            />
        </Box>
    );
}

// ─── Компонент: строка сотрудника ─────────────────────────────────────────────
function EmployeeRow({ employee, isSelf, canUpdate, canDelete, canResetPw, onEdit, onDelete, onResetPw, canFire, canRestore, onFire, onRestore, canSetSalary, onSetSalary }) {
    const cfg     = ROLE_CONFIG[employee.role] || ROLE_CONFIG.photographer;
    const blocked = employee.is_blocked;

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                display:      'flex',
                alignItems:   'center',
                gap:          1.5,
                px:           2,
                py:           1.5,
                borderRadius: 'var(--joy-radius-xl)',
                border:       '1px solid var(--joy-palette-divider)',
                bgcolor:      blocked ? 'rgba(248,113,113,0.03)' : 'var(--joy-palette-background-level1)',
                transition:   'all .15s',
                opacity:      blocked ? 0.7 : 1,
                '&:hover':    { borderColor: 'rgba(162,38,226,0.3)', bgcolor: blocked ? 'rgba(248,113,113,0.05)' : 'var(--joy-palette-background-level2)' },
            }}>
                {/* Аватар */}
                <Avatar sx={{
                    width: 40, height: 40, fontSize: 14, fontWeight: 700, flexShrink: 0,
                    bgcolor: blocked ? 'rgba(248,113,113,0.15)' : cfg.bg,
                    color:   blocked ? '#f87171' : cfg.color,
                    border:  `1.5px solid ${blocked ? 'rgba(248,113,113,0.3)' : 'var(--joy-palette-divider)'}`,
                    }}
                    src={employee.avatar_url || undefined}
                >
                    {(employee.first_name || employee.full_name || '').charAt(0).toUpperCase()}
                </Avatar>

                {/* Инфо */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                            {employee.full_name || `${employee.first_name} ${employee.last_name}`.trim() || employee.email}
                        </Typography>

                        <Chip
                            size="sm"
                            startDecorator={cfg.icon}
                            sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 10, border: 'none' }}
                        >
                            {employee.schedule_role_label || cfg.label}
                        </Chip>

                        {employee.custom_role_detail && (
                            <Chip
                                size="sm"
                                startDecorator={<SecurityRoundedIcon sx={{ fontSize: 10 }} />}
                                sx={{
                                    bgcolor: 'rgba(168,85,247,0.1)',
                                    color: '#a855f7',
                                    fontWeight: 700, fontSize: 10, border: 'none',
                                }}
                            >
                                {employee.custom_role_detail.name}
                            </Chip>
                        )}

                        {isSelf && (
                            <Chip size="sm" sx={{ bgcolor: 'rgba(162,38,226,0.08)', color: 'var(--joy-palette-primary-400)', fontSize: 10, border: 'none' }}>
                                Вы
                            </Chip>
                        )}

                        {blocked && (
                            <Chip
                                size="sm"
                                startDecorator={<BlockRoundedIcon sx={{ fontSize: 10 }} />}
                                sx={{ bgcolor: 'rgba(248,113,113,0.1)', color: '#f87171', fontSize: 10, border: 'none' }}
                            >
                                Заблокирован
                            </Chip>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mt: 0.4, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <EmailRoundedIcon sx={{ fontSize: 11 }} />
                            {employee.email}
                        </Typography>
                        {employee.phone && (
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <PhoneRoundedIcon sx={{ fontSize: 11 }} />
                                {employee.phone}
                            </Typography>
                        )}
                        {employee.date_of_birth && (
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                🎂 {new Date(employee.date_of_birth + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </Typography>
                        )}
                        {employee.is_fired && employee.fired_at && (
                            <Typography sx={{ fontSize: 11, color: '#f87171', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                ⛔ Уволен: {new Date(employee.fired_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Кнопки */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {canResetPw && (
                        <IconButton size="sm" variant="soft"
                            onClick={() => onResetPw(employee)} title="Сбросить пароль"
                            sx={{ bgcolor: 'rgba(245, 159, 11, 0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(245,158,11,0.18)' } }}
                        >
                            <LockResetRoundedIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                        </IconButton>
                    )}
                    {canUpdate && (
                        <IconButton size="sm" variant="soft"
                            onClick={() => onEdit(employee)}
                            sx={{ bgcolor: 'rgba(162,38,226,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(162,38,226,0.18)' } }}
                        >
                            <EditRoundedIcon sx={{ fontSize: 20, color: 'primary.300' }} />
                        </IconButton>
                    )}
                    {canSetSalary && (employee.role === 'admin' || employee.role === 'admin_photographer') && (
                        <IconButton size="sm" variant="soft"
                            onClick={() => onSetSalary(employee)} title="Оклад админа"
                            sx={{ bgcolor: 'rgba(34,197,94,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(34,197,94,0.18)' } }}
                        >
                            <PaidRoundedIcon sx={{ fontSize: 20, color: '#22c55e' }} />
                        </IconButton>
                    )}
                    {/* Уволить (только для активных) */}
                    {canFire && !isSelf && !employee.is_fired && (
                        <IconButton size="sm" variant="soft"
                            onClick={() => onFire(employee)}
                            title="Уволить"
                            sx={{
                                bgcolor: 'rgba(249,115,22,0.5)',
                                borderRadius: 'var(--joy-radius-md)',
                                '&:hover': { bgcolor: 'rgba(249,115,22,0.18)' },
                            }}
                        >
                            <LogoutRoundedIcon sx={{ fontSize: 20, color: '#f97316' }} />
                        </IconButton>
                    )}

                    {/* Восстановить (только для уволенных) */}
                    {canRestore && employee.is_fired && (
                        <IconButton size="sm" variant="soft"
                            onClick={() => onRestore(employee)}
                            title="Восстановить"
                            sx={{
                                bgcolor: 'rgba(34,197,94,0.5)',
                                borderRadius: 'var(--joy-radius-md)',
                                '&:hover': { bgcolor: 'rgba(34,197,94,0.18)' },
                            }}
                        >
                            <RestoreRoundedIcon sx={{ fontSize: 20, color: '#22c55e' }} />
                        </IconButton>
                    )}
                    {canDelete && !isSelf && (
                        <IconButton size="sm" variant="soft"
                            onClick={() => onDelete(employee)}
                            sx={{ bgcolor: 'rgba(248,113,113,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(248,113,113,0.18)' } }}
                        >
                            <DeleteRoundedIcon sx={{ fontSize: 20, color: '#f87171' }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}

// ─── Поле ввода пароля с глазиком ─────────────────────────────────────────────
function PasswordInput({ value, onChange, placeholder }) {
    const [show, setShow] = useState(false);
    return (
        <Input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={onChange}
            placeholder={placeholder || 'Минимум 6 символов'}
            endDecorator={
                <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => setShow(v => !v)}
                    sx={{ color: 'var(--joy-palette-text-tertiary)' }}
                >
                    {show
                        ? <VisibilityOffRoundedIcon sx={{ fontSize: 16 }} />
                        : <VisibilityRoundedIcon sx={{ fontSize: 16 }} />
                    }
                </IconButton>
            }
            sx={{
                bgcolor: 'var(--joy-palette-background-level2)',
                border: '1px solid var(--joy-palette-divider)',
                color: 'white',
                fontSize: 13,
                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
            }}
        />
    );
}

// ─── Поле формы ───────────────────────────────────────────────────────────────
function FormField({ label, children, error }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: error ? '#f87171' : 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                {label}
            </Typography>
            {children}
            {error && (
                <Typography sx={{ fontSize: 11, color: '#f87171', mt: 0.4, fontWeight: 600 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}


// ─── Модалка: создать / редактировать сотрудника ──────────────────────────────
function EmployeeModal({ open, employee, onClose, onSave, loading, error, roles = [] }) {
    const { token, showToast } = useContext(AppContext);
    const isEdit = !!employee;

    const [form, setForm] = useState({
        first_name: '', last_name: '', email: '',
        phone: '', role: 'photographer', password: '',
        is_blocked: false, is_excluded_from_payroll: false,
    });
    const [errs, setErrs] = useState({});
    const [newPassword, setNewPassword] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    

    const validate = () => {
        const e = {};
        if (!form.first_name?.trim()) e.first_name = 'Укажите имя';
        if (!isEdit) {
            if (!form.email?.trim())                       e.email    = 'Укажите email';
            else if (!/^\S+@\S+\.\S+$/.test(form.email))   e.email    = 'Некорректный email';
            // if (!form.phone?.trim())                       e.phone    = 'Укажите телефон';
            if (form.phone?.trim() && !/^[+\d\s()-]{6,20}$/.test(form.phone)) e.phone = 'Некорректный телефон';
            if (!form.password)                            e.password = 'Укажите пароль';
            else if (form.password.length < 6)             e.password = 'Минимум 6 символов';
        } else {
            if (form.phone && !/^[+\d\s()-]{6,20}$/.test(form.phone)) e.phone = 'Некорректный телефон';
        }
        return e;
    };


    useEffect(() => {
        if (open) {
            setErrs({});
            if (employee) {
                setForm({
                    first_name: employee.first_name || '',
                    last_name:  employee.last_name  || '',
                    email:      employee.email      || '',
                    phone:      employee.phone      || '',
                    role:       employee.role       || 'photographer',
                    custom_role_id: employee.custom_role || null,
                    password:   '',
                    is_blocked: employee.is_blocked || false,
                    is_excluded_from_payroll: employee.is_excluded_from_payroll || false,
                });
            } else {
                setForm({
                    first_name: '', last_name: '', email: '', phone: '',
                    role: 'photographer', custom_role_id: null, password: '',
                    is_blocked: false, is_excluded_from_payroll: false,
                });
            }
        }
    }, [open, employee]);

    const set = (field) => (e) => {
        const val = typeof e === 'string' ? e : e.target.value;
        setForm(prev => ({ ...prev, [field]: val }));
        setErrs(prev => ({ ...prev, [field]: undefined }));
    };
    const setBool = (field) => (val) => setForm(prev => ({ ...prev, [field]: val }));

    const handleChangePassword = async () => {
        if (newPassword.length < 6) {
            showToast('Пароль минимум 6 символов', 'warning');
            return;
        }
        setPwSaving(true);
        // id редактируемого сотрудника — тот же, что используется при сохранении формы
        const res = await APIResetEmployeePassword(token, employee?.id, newPassword);
        setPwSaving(false);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось сменить пароль'), 'danger'); return; }
        showToast('Пароль изменён ✓ (сотрудник войдёт с новым паролем)');
        setNewPassword('');
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' },
                },
            }}
            sx={{
                // Перехватываем фон по клику и предотвращаем фоновый скролл
                touchAction: 'none',
                overscrollBehavior: 'contain',
            }}
        >
            <ModalDialog
                sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border:  '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                    maxWidth: 480, width: '100%',
                    // Контейнер модалки — flex-column с фикс. шапкой и футером
                    p: 0,
                    maxHeight: { xs: '100dvh', sm: '90vh' },
                    height:    { xs: '100dvh', sm: 'auto' },
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)', top: 16, right: 16, zIndex: 2 }} />

                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    p: 3,
                    pb: 2,
                }}>
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'var(--joy-palette-text-primary)', letterSpacing: '-0.01em' }}>
                        {isEdit ? 'Редактировать сотрудника' : 'Новый сотрудник'}
                    </Typography>
                </Box>

                {isEdit && (
                    <AvatarUploader
                        employee={employee}
                        onUpdated={(updatedAccount) => {
                            // updatedAccount содержит свежий avatar_url
                            // Прокидываем наверх через onSave-related механизм,
                            // или просто триггерим перезагрузку списка.
                            // Простой вариант — событие в showToast и rely on saved.
                        }}
                    />
                )}

                <Stack spacing={1.8}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <FormField label="Имя *" error={errs.first_name}>
                            <Input value={form.first_name} onChange={set('first_name')} placeholder="Иван" sx={{ ...(errs.first_name && { borderColor: '#f87171' }) }} />
                        </FormField>
                        <FormField label="Фамилия">
                            <Input value={form.last_name} onChange={set('last_name')} placeholder="Иванов" />
                        </FormField>
                    </Box>

                    {!isEdit && (
                        <FormField label="Email *" error={errs.email}>
                            <Input type="email" value={form.email} onChange={set('email')} placeholder="ivan@example.com" sx={{  ...(errs.email && { borderColor: '#f87171' }) }} />
                        </FormField>
                    )}

                    <FormField label={isEdit ? "Телефон" : "Телефон *"} error={errs.phone}>
                        <Input value={form.phone} onChange={set('phone')} placeholder="+7 999 000 00 00" sx={{ ...(errs.phone && { borderColor: '#f87171' }) }} />
                    </FormField>

                    <FormField label="Должность *">
                        <Select
                            value={form.role}
                            onChange={(_, val) => val && setForm(prev => ({ ...prev, role: val }))}
                            sx={{
                                color: 'var(--joy-palette-text-primary)',
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13,
                                '&:hover': { borderColor: 'rgba(162,38,226,0.4)', bgcolor: 'var(--joy-palette-background-level2)' },
                            }}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Option value="photographer">Фотограф</Option>
                            <Option value="admin">Админ точки</Option>
                            <Option value="admin_photographer">Админ + Фотограф</Option>
                            <Option value="manager">Руководитель</Option>
                        </Select>
                    </FormField>

                    <FormField label="Набор прав">
                        <Select
                            value={form.custom_role_id || ''}
                            onChange={(_, val) => setForm(prev => ({ ...prev, custom_role_id: val || null }))}
                            placeholder="Выберите роль"
                            sx={{
                                color: 'var(--joy-palette-text-primary)',
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13,
                                '&:hover': { borderColor: 'rgba(162,38,226,0.4)', bgcolor: 'var(--joy-palette-background-level2)' },
                            }}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Option value="">Без роли (стандартный)</Option>
                            {roles.map(r => (
                                <Option key={r.id} value={r.id}>
                                    {r.name} ({r.permissions?.length ?? 0} прав)
                                </Option>
                            ))}
                        </Select>
                    </FormField>

                    {/* Превью прав выбранной роли */}
                    {form.custom_role_id && (() => {
                        const selectedRole = roles.find(r => r.id === form.custom_role_id);
                        if (!selectedRole) return null;
                        return (
                            <Box sx={{
                                p: 1.5, borderRadius: 'var(--joy-radius-lg)',
                                border: '1px solid rgba(162,38,226,0.15)',
                                bgcolor: 'rgba(162,38,226,0.04)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <SecurityRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-primary-400)' }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-primary-300)' }}>
                                        Права роли «{selectedRole.name}»
                                    </Typography>
                                </Box>
                                {selectedRole.permissions?.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selectedRole.permissions.map(p => (
                                            <Chip key={p.id || p.tag} size="sm" sx={{
                                                fontSize: 9, fontWeight: 600,
                                                bgcolor: 'rgba(162,38,226,0.08)',
                                                color: 'var(--joy-palette-text-secondary)',
                                                border: 'none',
                                            }}>
                                                {p.name || p.tag}
                                            </Chip>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                        {selectedRole.permissions_count ?? 0} прав (откройте роль для деталей)
                                    </Typography>
                                )}
                            </Box>
                        );
                    })()}

                    {!isEdit && (
                        <FormField label="Пароль *" error={errs.password}>
                            <PasswordInput value={form.password} onChange={set('password')} />
                        </FormField>
                    )}

                    {isEdit && (
                        <FormField label="Пароль">
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <PasswordInput value={newPassword} onChange={(v) => setNewPassword(typeof v === 'string' ? v : v.target.value)} />
                                <Button onClick={handleChangePassword} loading={pwSaving} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    Изменить пароль
                                </Button>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                                Показать старый пароль нельзя (он хранится в зашифрованном виде) —
                                можно задать новый.
                            </Typography>
                        </FormField>
                    )}

                    {isEdit && (
                        <FormField label="Статус">
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[
                                    { val: false, label: 'Активен',       color: '#22c55e', bg: 'rgba(34,197,94,0.1)'  },
                                    { val: true,  label: 'Заблокирован',  color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
                                ].map(opt => (
                                    <Box
                                        key={String(opt.val)}
                                        onClick={() => setForm(p => ({ ...p, is_blocked: opt.val }))}
                                        sx={{
                                            flex: 1, textAlign: 'center',
                                            py: 1, borderRadius: 'var(--joy-radius-md)',
                                            border: '1.5px solid',
                                            borderColor: form.is_blocked === opt.val ? opt.color : 'var(--joy-palette-divider)',
                                            bgcolor: form.is_blocked === opt.val ? opt.bg : 'transparent',
                                            cursor: 'pointer', transition: 'all .15s',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: form.is_blocked === opt.val ? opt.color : 'var(--joy-palette-text-secondary)' }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </FormField>
                    )}

                    {isEdit && (
                        <FormField label="Учёт в системе">
                            <Box
                                onClick={() => setForm(p => ({ ...p, is_excluded_from_payroll: !p.is_excluded_from_payroll }))}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid',
                                    borderColor: form.is_excluded_from_payroll
                                        ? 'rgba(245,158,11,0.5)'
                                        : 'var(--joy-palette-divider)',
                                    bgcolor: form.is_excluded_from_payroll
                                        ? 'rgba(245,158,11,0.08)'
                                        : 'var(--joy-palette-background-level2)',
                                    cursor: 'pointer',
                                    transition: 'all .15s',
                                }}
                            >
                                <Box sx={{
                                    width: 18, height: 18,
                                    borderRadius: 4,
                                    border: '2px solid',
                                    borderColor: form.is_excluded_from_payroll ? '#f59e0b' : 'var(--joy-palette-text-tertiary)',
                                    bgcolor: form.is_excluded_from_payroll ? '#f59e0b' : 'transparent',
                                    flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {form.is_excluded_from_payroll && (
                                        <Typography sx={{ color: '#fff', fontSize: 12, lineHeight: 1, fontWeight: 900 }}>✓</Typography>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                        Не учитывать в графиках, штрафах, премиях
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                        Сотрудник остаётся доступным в управлении, но скрыт из рабочих списков
                                    </Typography>
                                </Box>
                            </Box>
                        </FormField>
                    )}

                    {error && (
                        <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 12 }}>
                            {error}
                        </Alert>
                    )}
                </Stack>
                </Box>{/* /СКРОЛЛЯЩАЯСЯ ОБЛАСТЬ */}

                {/* STICKY-ФУТЕР с кнопками */}
                <Box sx={{
                    borderTop: '1px solid var(--joy-palette-divider)',
                    bgcolor: 'var(--joy-palette-background-level1)',
                    p: 2,
                    display: 'flex',
                    gap: 1,
                    flexShrink: 0,
                }}>
                    <Button
                        onClick={() => {
                            const e = validate();
                            setErrs(e);
                            if (Object.keys(e).length === 0) onSave(form);
                        }}
                        disabled={loading}
                        loading={loading}
                        sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                            color: '#fff', fontWeight: 700, border: 'none',
                            boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                            '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                        }}
                    >
                        {isEdit ? 'Сохранить' : 'Создать'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{
                            borderColor: 'var(--joy-palette-divider)',
                            color: 'var(--joy-palette-text-secondary)',
                            fontWeight: 600,
                            '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                        }}
                    >
                        Отмена
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

function AdminSalaryModal({ open, employee, onClose, onSave, loading, error }) {
    const [value, setValue] = useState('');
    const [date,  setDate]  = useState('');

    useEffect(() => {
        if (open) {
            setValue('');
            setDate(new Date().toISOString().slice(0, 10));   // сегодня, YYYY-MM-DD
        }
    }, [open]);

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 400, width: '100%', p: 3,
                maxHeight: { xs: '100dvh', sm: '90vh' },
                height:    { xs: '100dvh', sm: 'auto' },
                overflowY: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />
                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#22c55e' }}>
                        💰 Оклад администратора
                    </Typography>
                    {employee && (
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.4 }}>
                            {employee.full_name || employee.first_name}
                        </Typography>
                    )}
                </Box>

                {error && (
                    <Alert color="danger" variant="soft" sx={{ mb: 1.5 }}>{error}</Alert>
                )}

                <FormField label="Дата">
                    <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} size="sm" />
                </FormField>
                <FormField label="Сумма оклада, ₽">
                    <Input type="number" value={value} onChange={(e) => setValue(e.target.value)}
                        placeholder="Например: 3000" size="sm" />
                </FormField>

                <Button
                    onClick={() => onSave({ value, date })}
                    loading={loading}
                    disabled={loading || !value || parseFloat(value) < 0 || !date}
                    sx={{ mt: 1, fontWeight: 800, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
                >
                    Сохранить оклад
                </Button>
            </ModalDialog>
        </Modal>
    );
}

// ─── Модалка: сброс пароля ────────────────────────────────────────────────────
function ResetPasswordModal({ open, employee, onClose, onSave, loading, error }) {
    const [password, setPassword] = useState('');

    useEffect(() => { if (open) setPassword(''); }, [open]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: {
                    sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' },
                },
            }}
            sx={{
                // Перехватываем фон по клику и предотвращаем фоновый скролл
                touchAction: 'none',
                overscrollBehavior: 'contain',
            }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 400, width: '100%',
                p: 3,
                // На мобильных — фуллскрин-режим
                maxHeight: { xs: '100dvh', sm: '90vh' },
                height:    { xs: '100dvh', sm: 'auto' },
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#f59e0b', letterSpacing: '-0.01em' }}>
                        🔑 Сброс пароля
                    </Typography>
                    {employee && (
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.4 }}>
                            {employee.full_name || employee.first_name}
                        </Typography>
                    )}
                </Box>

                <Stack spacing={1.8}>
                    <FormField label="Новый пароль *">
                        <PasswordInput
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="Минимум 6 символов"
                        />
                    </FormField>

                    {error && (
                        <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 12 }}>
                            {typeof error === 'string' ? error : JSON.stringify(error)}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={() => onSave(password)}
                            loading={loading}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
                                color: '#fff', fontWeight: 700, border: 'none',
                                boxShadow: '0 4px 16px rgba(245,158,11,0.35)',
                            }}
                        >
                            Сбросить пароль
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

// ─── Модалка: удаление ────────────────────────────────────────────────────────
function DeleteModal({ open, employee, onClose, onConfirm, loading }) {
    const [step, setStep] = useState(1);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) { setStep(1); setReason(''); }
    }, [open]);

    const handleNext = () => setStep(2);
    const handleFinalConfirm = () => onConfirm(reason);

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } },
            }}
            sx={{ touchAction: 'none', overscrollBehavior: 'contain' }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 380, width: '100%', p: 3,
                maxHeight: { xs: '100dvh', sm: '90vh' },
                height:    { xs: '100dvh', sm: 'auto' },
                overflowY: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '50%',
                        bgcolor: 'rgba(248,113,113,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.5,
                    }}>
                        <DeleteRoundedIcon sx={{ fontSize: 24, color: '#f87171' }} />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                        {step === 1 ? 'Отправить в корзину?' : 'Точно отправить?'}
                    </Typography>
                    {employee && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                            {employee.full_name || employee.first_name}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 1.5, lineHeight: 1.4 }}>
                        {step === 1
                            ? 'Аккаунт уйдёт в корзину на 14 дней — за это время его можно восстановить. После — удалится навсегда.'
                            : 'Это второе подтверждение. После — отменить будет уже сложнее.'
                        }
                    </Typography>
                </Box>

                {step === 1 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>
                            Причина (необязательно)
                        </Typography>
                        <Input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Например: уволен"
                            sx={{ bgcolor: 'var(--joy-palette-background-level2)', fontSize: 13 }}
                        />
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={step === 1 ? handleNext : handleFinalConfirm}
                        loading={loading}
                        sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                            color: '#fff', fontWeight: 700, border: 'none',
                            boxShadow: '0 4px 16px rgba(248,113,113,0.35)',
                        }}
                    >
                        {step === 1 ? 'Далее' : 'В корзину'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={step === 1 ? onClose : () => setStep(1)}
                        sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}
                    >
                        {step === 1 ? 'Отмена' : 'Назад'}
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  EmployeesPage
// ════════════════════════════════════════════════════════════════════
export default function EmployeesPage() {
    const { token, account, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate   = hasPerm('employees.create');
    const canUpdate   = hasPerm('employees.update');
    const canDelete   = hasPerm('employees.delete');
    const canResetPw  = hasPerm('employees.reset_password');
    const canFire     = hasPerm('employees.fire') || hasPerm('employees.restore');
    const canSetSalary = hasPerm('finance.admin_salaries.set');

    const [employees, setEmployees] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [search,    setSearch]    = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [employeeStatus, setEmployeeStatus] = useState('active'); // active | fired
    const [roles, setRoles] = useState([]);

    // Модалки
    const [editModal,   setEditModal]   = useState({ open: false, employee: null, loading: false, error: '' });
    const [deleteModal, setDeleteModal] = useState({ open: false, employee: null, loading: false });
    const [pwModal,     setPwModal]     = useState({ open: false, employee: null, loading: false, error: '' });
    const [salaryModal, setSalaryModal] = useState({ open: false, employee: null, loading: false, error: '' });
    const [fireModal, setFireModal] = useState({
        open: false,
        employee: null,
        mode: 'fire',   // 'fire' | 'restore'
    });

    const loadEmployees = useCallback(async () => {
        setLoading(true);
        const data = await APIGetEmployees(token, { status: 'all' });
        if (Array.isArray(data)) setEmployees(data);

        // Загружаем роли с деталями (для превью прав)
        const rolesData = await APIGetRoles(token);
        if (Array.isArray(rolesData)) {
            // Подгружаем detail для каждой роли
            const detailed = await Promise.all(
                rolesData.map(async (r) => {
                    const d = await APIGetRoleDetail(token, r.id);
                    return d?.id ? d : r;
                })
            );
            setRoles(detailed);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => { loadEmployees(); }, [loadEmployees]);

    // ── Счётчики статусов (по всем сотрудникам, без учёта поиска/роли) ──
    const { activeCount, firedCount } = useMemo(() => {
        const non_ws = employees.filter(e => e.role !== 'workstation');
        return {
            activeCount: non_ws.filter(e => !e.is_fired).length,
            firedCount:  non_ws.filter(e =>  e.is_fired).length,
        };
    }, [employees]);

    // ── Фильтрация ────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        const list = employees.filter(e => {
            // Рабочие точки на этой странице не показываем — у них свой раздел
            if (e.role === 'workstation') return false;

            // Фильтр по статусу (уволенные / активные)
            const isFired = !!e.is_fired;
            if (employeeStatus === 'active' &&  isFired) return false;
            if (employeeStatus === 'fired'  && !isFired) return false;

            const matchSearch = !q ||
                e.first_name?.toLowerCase().includes(q) ||
                e.last_name?.toLowerCase().includes(q) ||
                e.email?.toLowerCase().includes(q) ||
                e.phone?.includes(q);
            const matchRole = roleFilter === 'all' || e.role === roleFilter;
            return matchSearch && matchRole;
        });
        // Уволенные — по дате увольнения, последние сверху
        if (employeeStatus === 'fired') {
            list.sort((a, b) => new Date(b.fired_at || 0) - new Date(a.fired_at || 0));
        }
        return list;
    }, [employees, employeeStatus, search, roleFilter]);

    const photographers    = filtered.filter(e => e.role === 'photographer');
    const admins           = filtered.filter(e => e.role === 'admin');
    const adminPhotogs     = filtered.filter(e => e.role === 'admin_photographer');
    const managers         = filtered.filter(e => e.role === 'manager');

    // Открыть модалку увольнения
    const handleOpenFire = (employee) => {
        setFireModal({ open: true, employee, mode: 'fire' });
    };

    // Открыть модалку восстановления
    const handleOpenRestore = (employee) => {
        setFireModal({ open: true, employee, mode: 'restore' });
    };

    // После успешного увольнения / восстановления — перезагрузить список
    const handleFireSaved = () => {
        setFireModal({ open: false, employee: null, mode: 'fire' });
        loadEmployees();   // или triggerRefresh(), в зависимости от того, как обновляете
    };

    // ── Создать / редактировать ───────────────────────────────────
    const handleSaveEmployee = async (form) => {
        const isEdit = !!editModal.employee;
        setEditModal(prev => ({ ...prev, loading: true, error: '' }));

        let res;
        if (isEdit) {
            const payload = {
                first_name: form.first_name,
                last_name:  form.last_name,
                phone:      form.phone,
                role:       form.role,
                is_blocked: form.is_blocked,
                custom_role_id: form.custom_role_id || null,
                ...(form.role_id && { role_id: form.role_id }),
            };
            res = await APIUpdateEmployee(token, editModal.employee.id, payload);
        } else {
            res = await APICreateEmployee(token, form);
        }

        if (res?.error) {
            setEditModal(prev => ({
                ...prev, loading: false,
                error: parseApiError(res.error, 'Не удалось сохранить сотрудника'),
            }));
            return;
        }

        showToast(isEdit ? 'Сотрудник обновлён ✓' : 'Сотрудник создан ✓');
        setEditModal({ open: false, employee: null, loading: false, error: '' });
        loadEmployees();
    };

    // ── Удалить ───────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleteModal(prev => ({ ...prev, loading: true }));
        const reason = deleteModal.reason || '';
        const res = await APITrashEmployee(token, deleteModal.employee.id, reason);
        setDeleteModal({ open: false, employee: null, loading: false, reason: '' });
        if (!res?.error) {
            showToast('Отправлен в корзину. Восстановить можно 14 дней.', 'warning');
            loadEmployees();
        } else {
            showToast(parseApiError(res.error, 'Ошибка при удалении'), 'danger');
        }
    };

    const handleSaveSalary = async ({ value, date }) => {
        const emp = salaryModal.employee;
        if (!emp) return;
        setSalaryModal(prev => ({ ...prev, loading: true, error: '' }));
        const res = await APISetAdminSalary(token, {
            administrator_id: emp.id,
            value: parseFloat(value),
            date,
        });
        if (res?.error) {
            setSalaryModal(prev => ({ ...prev, loading: false,
                error: parseApiError(res.error, 'Не удалось сохранить оклад') }));
            return;
        }
        setSalaryModal({ open: false, employee: null, loading: false, error: '' });
        showToast('Оклад сохранён ✓');
    };

    // ── Сбросить пароль ───────────────────────────────────────────
    const handleResetPassword = async (newPassword) => {
        if (!newPassword || newPassword.length < 6) {
            setPwModal(prev => ({ ...prev, error: 'Пароль должен быть не менее 6 символов' }));
            return;
        }
        setPwModal(prev => ({ ...prev, loading: true, error: '' }));
        const res = await APIResetEmployeePassword(token, pwModal.employee.id, newPassword);
        if (!res?.error) {
            showToast('Пароль сброшен ✓', 'success');
            setPwModal({ open: false, employee: null, loading: false, error: '' });
        } else {
            const errMsg = typeof res.error === 'string' ? res.error : JSON.stringify(res.error);
            setPwModal(prev => ({ ...prev, loading: false, error: errMsg }));
        }
    };

    const renderGroup = (list, label) => {
        if (list.length === 0) return null;
        return (
            <Box>
                <Typography sx={{
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: 'var(--joy-palette-text-tertiary)',
                    mb: 1,
                }}>
                    {label} ({list.length})
                </Typography>
                <motion.div variants={stagger} initial="hidden" animate="visible">
                    <Stack spacing={1}>
                        {list.map(emp => (
                            <EmployeeRow
                                key={emp.id}
                                employee={emp}
                                isSelf={emp.id === account?.id}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                                canResetPw={canResetPw}
                                canFire={hasPerm('employees.fire')} 
                                canRestore={hasPerm('employees.restore')} 
                                onEdit={(e) => navigate(`/manager/employees/${emp.id}/edit`)}
                                onDelete={(e) => setDeleteModal({ open: true, employee: e, loading: false })}
                                onResetPw={(e) => setPwModal({ open: true, employee: e, loading: false, error: '' })}
                                onFire={handleOpenFire} 
                                onRestore={handleOpenRestore}
                                canSetSalary={canSetSalary}
                                onSetSalary={(e) => setSalaryModal({ open: true, employee: e, loading: false, error: '' })}
                            />
                        ))}
                    </Stack>
                </motion.div>
            </Box>
        );
    };

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                onClick={() => navigate('/manager')}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 36, height: 36,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                                }}
                            >
                                <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Сотрудники
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                    {employees.filter(e => !e.is_fired).length} чел. в команде
                                </Typography>
                            </Box>
                        </Box>
                            
                        <Stack direction={'row'} justifyContent={'flex-end'} sx={{ gap: 1 }}>
                            <Button
                                variant="soft" color="neutral" size="sm"
                                startDecorator={<DeleteRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => navigate('/employees/trashed')}
                            >
                                Корзина
                            </Button>
                            {canCreate && (
                                <Button
                                    startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => navigate('/manager/employees/new')}
                                    sx={{
                                        background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                        color: '#fff', fontWeight: 700, fontSize: 13,
                                        border: 'none',
                                        boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                        '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                    }}
                                >
                                    Добавить
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </motion.div>

                {/* ── Переключатель статуса ─────────────────────────────────── */}
                <Box sx={{
                    display: 'inline-flex',
                    p: 0.4,
                    mb: 2,
                    borderRadius: 'var(--joy-radius-lg)',
                    border: '1px solid var(--joy-palette-divider)',
                    bgcolor: 'var(--joy-palette-background-level1)',
                    gap: 0.3,
                }}>
                    {[
                        {
                            value: 'active',
                            label: 'Активные',
                            count: employees.filter(e => !e.is_fired).length,
                            color: '#22c55e',
                            bg: 'rgba(34,197,94,0.10)',
                        },
                        {
                            value: 'fired',
                            label: 'Уволенные',
                            count: employees.filter(e => e.is_fired).length,
                            color: '#f97316',
                            bg: 'rgba(249,115,22,0.10)',
                        },
                    ].map(opt => {
                        const active = employeeStatus === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                onClick={() => setEmployeeStatus(opt.value)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.8,
                                    px: 1.8, py: 0.9,
                                    borderRadius: 'var(--joy-radius-md)',
                                    bgcolor: active ? opt.bg : 'transparent',
                                    border: '1px solid',
                                    borderColor: active ? `${opt.color}40` : 'transparent',
                                    cursor: 'pointer',
                                    transition: 'all .15s',
                                    '&:hover': {
                                        bgcolor: active ? opt.bg : 'var(--joy-palette-background-level2)',
                                    },
                                }}
                            >
                                <Typography sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: active ? opt.color : 'var(--joy-palette-text-secondary)',
                                    transition: 'color .15s',
                                }}>
                                    {opt.label}
                                </Typography>
                                <Chip
                                    size="sm"
                                    sx={{
                                        bgcolor: active ? `${opt.color}25` : 'var(--joy-palette-background-level2)',
                                        color: active ? opt.color : 'var(--joy-palette-text-tertiary)',
                                        fontWeight: 800,
                                        fontSize: 10,
                                        minHeight: 18,
                                        px: 0.8,
                                        border: 'none',
                                    }}
                                >
                                    {opt.count}
                                </Chip>
                            </Box>
                        );
                    })}
                </Box>

                {/* Поиск и фильтр */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.08 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
                        <Input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Поиск по имени, email, телефону…"
                            startDecorator={<SearchRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-tertiary)' }} />}
                            sx={{
                                flex: 1, minWidth: 200,
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13,
                                color: 'white',
                                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                            }}
                        />
                        <Select
                            value={roleFilter}
                            onChange={(_, val) => val && setRoleFilter(val)}
                            sx={{
                                color: 'var(--joy-palette-text-primary)',
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13,
                                '&:hover': { borderColor: 'rgba(162,38,226,0.4)', bgcolor: 'var(--joy-palette-background-level2)' },
                            }}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Option value="all">Все роли</Option>
                            <Option value="photographer">Фотографы</Option>
                            <Option value="admin">Админы точек</Option>
                            <Option value="admin_photographer">Админ + Фотограф</Option>
                            <Option value="manager">Руководители</Option>
                        </Select>
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2,
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                    }}>
                        <PersonRoundedIcon sx={{ fontSize: 32, color: 'var(--joy-palette-text-tertiary)' }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            {search ? 'Никого не найдено' : 'Нет сотрудников'}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {renderGroup(photographers, 'Фотографы')}
                        {renderGroup(admins, 'Админы точек')}
                        {renderGroup(adminPhotogs, 'Админ + Фотограф')}
                        {renderGroup(managers, 'Руководители')}
                    </Stack>
                )}
            </Box>

            {/* Модалки */}
            <EmployeeModal
                open={editModal.open}
                employee={editModal.employee}
                loading={editModal.loading}
                error={editModal.error}
                roles={roles}
                onClose={() => setEditModal({ open: false, employee: null, loading: false, error: '' })}
                onSave={handleSaveEmployee}
            />

            <DeleteModal
                open={deleteModal.open}
                employee={deleteModal.employee}
                loading={deleteModal.loading}
                onClose={() => setDeleteModal({ open: false, employee: null, loading: false })}
                onConfirm={(reason) => {
                    setDeleteModal(prev => ({ ...prev, reason }));
                    handleDelete();
                }}
            />

            <AdminSalaryModal
                open={salaryModal.open}
                employee={salaryModal.employee}
                loading={salaryModal.loading}
                error={salaryModal.error}
                onClose={() => setSalaryModal({ open: false, employee: null, loading: false, error: '' })}
                onSave={handleSaveSalary}
            />
            
            <ResetPasswordModal
                open={pwModal.open}
                employee={pwModal.employee}
                loading={pwModal.loading}
                error={pwModal.error}
                onClose={() => setPwModal({ open: false, employee: null, loading: false, error: '' })}
                onSave={handleResetPassword}
            />

            <FireEmployeeModal
                open={fireModal.open}
                employee={fireModal.employee}
                mode={fireModal.mode}
                onClose={() => setFireModal({ open: false, employee: null, mode: 'fire' })}
                onSaved={handleFireSaved}
            />
        </motion.div>
    );
}